<template>
  <div id="app-wrapper" class="ix" :data-theme="$store.state.isDarkMode ? 'dark' : 'light'">

    <div class="bg-orbs">
      <span></span>
      <span></span>
      <span></span>
    </div>

    <v-idle
        class="d-none"
        :duration="15 * 60"
        :events="['mousemove', 'keydown', 'mousedown', 'touchstart']"
        @idle="onIdle"
        @active="onActive"
    />

    <main id="mainpaage" class="main-content position-relative max-height-vh-100 h-100 overflow-x-hidden">
      <router-view />
      <configurator class="no-print"
          :toggle="toggleConfigurator"
          :class="[showConfig ? 'show' : '', hideConfigButton ? 'd-none' : '']"
      />
      <!-- Global progress toast for long-running export/remove batches.
           Self-registers its Vuex module on mount so store/index.js
           doesn't need editing. Visible whenever a batch is in flight,
           regardless of which module the user is currently in. -->
      <export-progress-toast />
      <mobile-bottom-nav />
    </main>
  </div>
</template>

<script>
import Configurator from "@/fragments/Configurator.vue";
import ExportProgressToast from "@/fragments/ExportProgressToast.vue";
import MobileBottomNav from "@/fragments/MobileBottomNav.vue";
import exportProgressModule from "@/store/modules/exportProgress";
import { mapMutations, mapState } from "vuex";
//import SubscriptionBanner from "@/components/SubscriptionBanner.vue";
import ApiService from "@/apis/apiService";
import router from "@/router";
import { Chart } from 'chart.js';

// ─── Chart.js detached-canvas guard ────────────────────────────────
//
// When a Vue component that owns a Chart.js instance navigates away
// before the chart is fully torn down, the chart can end up in
// Chart.js's global Animator registry with a canvas that's been
// removed from the DOM. The Animator ticks every ~16ms and calls
// chart.draw() on every registered chart, which calls clear(), which
// calls canvas.getContext('2d') — and on a detached canvas that
// returns null, blowing up with:
//
//   Cannot read properties of null (reading 'getContext')
//
// In the wild this is almost always a route component that didn't
// destroy its chart in beforeUnmount, OR an async chart-creation
// callback that races with the route change. Fixing every offender
// individually is the right long-term answer, but in the meantime
// a top-level guard here turns the crash into a one-line warning
// (which lets us identify WHICH chart is offending) without taking
// the whole app down.
//
// We patch Chart.prototype.clear because it's the lowest-cost
// place: clear() is the first thing draw() calls, so guarding here
// short-circuits draw entirely when the canvas is gone. No harm
// done — a detached chart had nothing to draw anyway.
const _chartClear = Chart.prototype.clear;
Chart.prototype.clear = function patchedClear() {
  if (!this.canvas || !this.canvas.isConnected || !this.ctx) {
    // Self-destruct so the Animator stops calling us. destroy()
    // removes this instance from Chart.instances and cancels any
    // queued animations.
    if (typeof this.destroy === 'function') {
      try { this.destroy(); } catch (_) { /* already mid-teardown */ }
    }
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(
        '[chart.js guard] Detached canvas detected — destroyed chart',
        this.id,
        'created with type', this.config && this.config.type,
        '. Find the component that created this chart and call ' +
        'chart.destroy() in its beforeUnmount hook.'
      );
    }
    return this;
  }
  return _chartClear.apply(this, arguments);
};

export default {
  name: 'App',
  components: {
    //SubscriptionBanner,
    Configurator,
    ExportProgressToast,
    MobileBottomNav,
  },
  beforeCreate() {
    // Register the export-progress Vuex module before any child
    // component's setup runs. Doing this here (rather than in the
    // toast's own created()) ensures the toast's computed properties
    // can read $store.state.exportProgress without a race on first
    // render. Idempotent — hasModule() guards against double-register
    // on hot-reload.
    if (!this.$store.hasModule('exportProgress')) {
      this.$store.registerModule('exportProgress', exportProgressModule);
    }
  },
  data() {
    return {
      subscriptionWarning: null
    };
  },
  methods: {
    ...mapMutations(["toggleConfigurator", "navbarMinimize"]),
    onIdle() {
      console.log("⏳ User inactive, logging out...");
      this.$store.dispatch("logout");

      this.logout();
    },
    onActive() {
      console.log("User is active again.");
    },
    logout() {
      localStorage.clear();
      router.push('/');
    },
  },
  created() {
    // Interceptor to capture warning headers
    ApiService.apiClient.interceptors.response.use(response => {
      const warning = response.headers['x-subscription-warning'];
      if (warning) {
        this.subscriptionWarning = warning;
      }
      return response;
    });
  },
  computed: {
    ...mapState([
      "isAbsolute",
      "navbarFixed",
      "absolute",
      "showSidenav",
      "showNavbar",
      "showConfig",
      "hideConfigButton"
    ])
  },
}
</script>

<style>
/* Banner fixed at top of viewport */
.subscription-banner {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1200;
  background-color: #fff3cd;
  border-bottom: 1px solid #ffeeba;
  padding: 0.75rem 1rem;
  max-height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* ══════════════════════════════════════════════════════════════════════
   GLOBAL THEME VARIABLES & ORB ANIMATIONS
   ══════════════════════════════════════════════════════════════════════ */

[data-theme="dark"] {
  --bg: #121424; /* Solid dark navy */
  --bg-card: rgba(255, 255, 255, 0.035);
  --bg-card-hover: rgba(255, 255, 255, 0.065);
  --border: rgba(255, 255, 255, 0.07);
  --border-hover: rgba(255, 255, 255, 0.14);
  --text-1: #eef0f6;
  --text-2: #7b7f96;
  --shadow: rgba(0, 0, 0, 0.35);
  --orb-opacity: 0.35;
  --toggle-bg: rgba(255, 255, 255, 0.08);
  --today-bg: linear-gradient(135deg, rgba(79, 140, 255, 0.1), rgba(155, 109, 255, 0.06));
  --today-border: rgba(79, 140, 255, 0.18);
  --bnav-bg: rgba(11, 13, 23, 0.94);
  --fade-to: rgba(11, 13, 23, 0.85);
  --dropdown-bg: rgba(18, 20, 36, 0.97);

  /* Shared aliases used by transport/registration-style pages */
  --card: var(--bg-card);
  --surface-2: rgba(255, 255, 255, 0.045);
  --input-bg: rgba(255, 255, 255, 0.055);
  --border-2: rgba(255, 255, 255, 0.12);
  --text-3: #a1a6bb;
}


[data-theme="light"] {
  --bg: #f4f5f9; /* Solid light color */
  --bg-card: rgba(255, 255, 255, 0.75);
  --bg-card-hover: rgba(255, 255, 255, 0.92);
  --border: rgba(0, 0, 0, 0.07);
  --border-hover: rgba(0, 0, 0, 0.14);
  --text-1: #1a1c2e;
  --text-2: #6b6e82;
  --shadow: rgba(0, 0, 0, 0.08);

  /* Shared aliases used by transport/registration-style pages */
  --card: var(--bg-card);
  --surface-2: #f8fafc;
  --input-bg: #ffffff;
  --border-2: rgba(0, 0, 0, 0.12);
  --text-3: #94a3b8;
  --orb-opacity: 0.08;
  --toggle-bg: rgba(0, 0, 0, 0.06);
  --today-bg: linear-gradient(135deg, rgba(79, 140, 255, 0.08), rgba(155, 109, 255, 0.04));
  --today-border: rgba(79, 140, 255, 0.15);
  --bnav-bg: rgba(244, 245, 249, 0.94);
  --fade-to: rgba(244, 245, 249, 0.85);
  --dropdown-bg: rgba(255, 255, 255, 0.97);
}

/* APP ROOT & APP COLORS */
.ix {
  --theme-gradient: linear-gradient(135deg, #ffd54f 0%, #008080 70%);
  --theme-gold: #ffd54f;
  --theme-pink: #008080;

  --accent-blue: #4f8cff;
  --accent-purple: #9b6dff;
  --accent-green: #2dd4a8;
  --accent-orange: #ff8c42;
  --accent-pink: #ff6b9d;
  --accent-cyan: #22d3ee;
  --accent-yellow: #fbbf24;
  --accent-red: #f87171;

  font-family: 'DM Sans', sans-serif;
  background: transparent !important; /* Wrapper stays transparent to prevent stacking issues */
  color: var(--text-1);
  min-height: 100vh;
  overflow-x: hidden;
  transition: color 0.4s ease;
  position: relative;
}

/* GLOBAL ANIMATED ORBS & BACKGROUND */
.bg-orbs {
  position: fixed;
  inset: 0;
  z-index: -1; /* Safely stays behind everything, modal issues fixed */
  pointer-events: none;
  overflow: hidden;
  background: var(--bg); /* Applies the solid color here instead! */
  transition: background 0.4s ease;
}

.bg-orbs span {
  position: absolute;
  border-radius: 50%;
  filter: blur(110px);
  opacity: var(--orb-opacity);
  animation: orbF 20s ease-in-out infinite alternate;
  transition: opacity 0.4s;
}

.bg-orbs span:nth-child(1) { width: 420px; height: 420px; background: var(--theme-pink); top: -8%; left: -4%; }
.bg-orbs span:nth-child(2) { width: 340px; height: 340px; background: var(--theme-gold); bottom: -8%; right: -4%; animation-delay: -7s; }
.bg-orbs span:nth-child(3) { width: 260px; height: 260px; background: var(--accent-orange); top: 40%; left: 50%; animation-delay: -14s; }

@keyframes orbF {
  0% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(25px, -20px) scale(1.06); }
  100% { transform: translate(-12px, 25px) scale(0.96); }
}
</style>
